'use client';

import { UseQueryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { Activity, getActivitiesFeatured } from '../services/activities.service';

interface UseActivitiesFeaturedParams {
  latitude?: number | null;
  longitude?: number | null;
  limit?: number;
  cursor?: string;
}

/**
 * Custom hook to fetch featured activities using React Query.
 * Fetches data based on locale and optional location parameters.
 * Refetches automatically when location parameters change.
 *
 * @param params Object containing optional latitude, longitude, limit, cursor.
 * @param options Optional React Query options, including `enabled` to control when the query runs.
 * @returns React Query result object for featured activities.
 */
export function useActivitiesFeatured(
  params: UseActivitiesFeaturedParams = {},
  options?: Omit<UseQueryOptions<Activity[], Error>, 'queryKey' | 'queryFn'>
) {
  const { latitude, longitude, limit, cursor } = params;
  const locale = useLocale();

  return useQuery<Activity[], Error>({
    queryKey: ['activitiesFeatured', locale, latitude, longitude, limit, cursor],
    queryFn: () => getActivitiesFeatured({ locale, latitude, longitude, limit, cursor }),
    staleTime: 5 * 60 * 1000,
    // Spread any additional options passed in (like enabled)
    ...options
  });
}

/**
 * Suspense-enabled version of useActivitiesFeatured.
 * Use this with React Suspense boundaries for cleaner loading states.
 * The component will suspend while data is loading.
 */
export function useActivitiesFeaturedSuspense(params: UseActivitiesFeaturedParams = {}) {
  const { latitude, longitude, limit, cursor } = params;
  const locale = useLocale();

  return useSuspenseQuery<Activity[], Error>({
    queryKey: ['activitiesFeatured', locale, latitude, longitude, limit, cursor],
    queryFn: () => getActivitiesFeatured({ locale, latitude, longitude, limit, cursor }),
    staleTime: 5 * 60 * 1000
  });
}
